import * as i0 from '@angular/core';
import { Injectable, Directive, Host, Input, NgModule } from '@angular/core';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { Subject, ReplaySubject, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, tap, switchMap, takeUntil, pluck, startWith, pairwise, take } from 'rxjs/operators';

class AbstractPortalHostDirective {
    constructor(hostRef, portalService, componentFactoryResolver, injector, appRef) {
        this.hostRef = hostRef;
        this.portalService = portalService;
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this.appRef = appRef;
        this.destroyed$ = new Subject();
        this.portalOutlet = null;
        this.slot$ = new ReplaySubject();
        this.attached = false;
    }
    afterViewInit() {
        this.portalOutlet = new DomPortalOutlet(this.hostRef.nativeElement, this.componentFactoryResolver, this.appRef, this.injector);
        this.slot$
            .pipe(distinctUntilChanged(), tap(() => this.updateContentAttachment(null)), switchMap(slot => this.portalService.getSlotListener(slot)), takeUntil(this.destroyed$))
            .subscribe((portal) => this.updateContentAttachment(portal), () => {
        }, () => this.updateContentAttachment(null));
    }
    onDestroy() {
        this.destroyed$.next();
    }
    updateContentAttachment(portal) {
        var _a, _b, _c, _d;
        if ((_a = this.portalOutlet) === null || _a === void 0 ? void 0 : _a.hasAttached()) {
            this.portalOutlet.detach();
        }
        if (portal) {
            (_b = this.portalOutlet) === null || _b === void 0 ? void 0 : _b.attach(portal);
        }
        this.attached = (_d = (_c = this.portalOutlet) === null || _c === void 0 ? void 0 : _c.hasAttached()) !== null && _d !== void 0 ? _d : false;
    }
}

class PortalService {
    constructor() {
        this.slots$ = new BehaviorSubject({});
    }
    attach(slot, portal) {
        this.slots$.next(Object.assign(Object.assign({}, this.slots$.value), { [slot]: portal }));
    }
    detach(slot) {
        const slots = Object.entries(this.slots$.value)
            .filter(([key]) => key !== slot)
            .reduce((prev, [key, value]) => ({
            [key]: value,
        }), {});
        this.slots$.next(slots);
    }
    getSlotListener(slot) {
        return this.slots$
            .pipe(pluck(slot));
    }
}
PortalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PortalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class PortalHostDirective extends AbstractPortalHostDirective {
    constructor(hostRef, portalService, componentFactoryResolver, injector, appRef) {
        super(hostRef, portalService, componentFactoryResolver, injector, appRef);
    }
    set rpPortalHost(value) {
        this.slot$.next(value);
    }
    ngAfterViewInit() {
        this.afterViewInit();
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
PortalHostDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalHostDirective, deps: [{ token: i0.ElementRef, host: true }, { token: PortalService }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Directive });
PortalHostDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.1", type: PortalHostDirective, selector: "[rpPortalHost]", inputs: { rpPortalHost: "rpPortalHost" }, exportAs: ["rpPortalHost"], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalHostDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rpPortalHost]',
                    exportAs: 'rpPortalHost',
                }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Host
                    }] }, { type: PortalService }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }];
    }, propDecorators: { rpPortalHost: [{
                type: Input
            }] } });

class PortalSlotDirective {
    constructor(templateRef, portalService, viewContainerRef) {
        this.templateRef = templateRef;
        this.portalService = portalService;
        this.viewContainerRef = viewContainerRef;
        this.destroyed$ = new Subject();
        this.slot$ = new ReplaySubject();
    }
    set rpPortalSlot(slot) {
        this.slot$.next(slot);
    }
    ngAfterViewInit() {
        const portal = new TemplatePortal(this.templateRef, this.viewContainerRef);
        this.slot$
            .pipe(takeUntil(this.destroyed$), startWith(undefined), distinctUntilChanged(), pairwise())
            .subscribe(([oldSlot, newSlot]) => {
            if (oldSlot) {
                this.portalService.detach(oldSlot);
            }
            if (newSlot) {
                this.portalService.attach(newSlot, portal);
            }
        });
    }
    ngOnDestroy() {
        this.slot$
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe(slot => slot && this.portalService.detach(slot));
        this.destroyed$.next();
    }
}
PortalSlotDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalSlotDirective, deps: [{ token: i0.TemplateRef, host: true }, { token: PortalService }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
PortalSlotDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.1", type: PortalSlotDirective, selector: "ng-template[rpPortalSlot]", inputs: { rpPortalSlot: "rpPortalSlot" }, exportAs: ["rpPortalSlot"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalSlotDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ng-template[rpPortalSlot]',
                    exportAs: 'rpPortalSlot',
                }]
        }], ctorParameters: function () {
        return [{ type: i0.TemplateRef, decorators: [{
                        type: Host
                    }] }, { type: PortalService }, { type: i0.ViewContainerRef }];
    }, propDecorators: { rpPortalSlot: [{
                type: Input
            }] } });

class PortalHostComponentStyleDirective extends AbstractPortalHostDirective {
    constructor(hostRef, portalService, componentFactoryResolver, injector, appRef) {
        super(hostRef, portalService, componentFactoryResolver, injector, appRef);
    }
    set slot(value) {
        this.slot$.next(value);
    }
    ngAfterViewInit() {
        this.afterViewInit();
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
PortalHostComponentStyleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalHostComponentStyleDirective, deps: [{ token: i0.ElementRef, host: true }, { token: PortalService }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Directive });
PortalHostComponentStyleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.1", type: PortalHostComponentStyleDirective, selector: "rp-portal-host", inputs: { slot: "slot" }, providers: [{ provide: PortalHostDirective, useExisting: PortalHostComponentStyleDirective }], exportAs: ["rpPortalHost"], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalHostComponentStyleDirective, decorators: [{
            type: Directive,
            args: [{
                    // tslint:disable-next-line:directive-selector
                    selector: 'rp-portal-host',
                    providers: [{ provide: PortalHostDirective, useExisting: PortalHostComponentStyleDirective }],
                    exportAs: 'rpPortalHost',
                }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Host
                    }] }, { type: PortalService }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }];
    }, propDecorators: { slot: [{
                type: Input
            }] } });

class PortalModule {
}
PortalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PortalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalModule, declarations: [PortalHostDirective,
        PortalSlotDirective,
        PortalHostComponentStyleDirective], exports: [PortalHostDirective,
        PortalSlotDirective,
        PortalHostComponentStyleDirective] });
PortalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.1", ngImport: i0, type: PortalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PortalHostDirective,
                        PortalSlotDirective,
                        PortalHostComponentStyleDirective,
                    ],
                    exports: [
                        PortalHostDirective,
                        PortalSlotDirective,
                        PortalHostComponentStyleDirective,
                    ],
                }]
        }] });

/*
 * Public API Surface of ng-portal
 */

/**
 * Generated bundle index. Do not edit.
 */

export { PortalHostComponentStyleDirective, PortalHostDirective, PortalModule, PortalSlotDirective };
